import { useEffect } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Search } from "@material-ui/icons";
import { Layout, useUserStore } from "@roc/feature-app-core";
import { useStore } from "@roc/client-portal-shared/hooks";
import { Paper } from "@roc/ui";
import { observer } from "mobx-react";
import { useAddAppraisalStore } from "./useAddAppraisalStore";
import { AddAppraisalSearchBar } from "./addAppraisalSearchBar";
import { AppraisalDetails, QuestionnairePrompt, AppraisalDetailsHeader } from '@roc/feature-documents';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: "1400px",
  },
  card: {
    padding: theme.spacing(3, 4),
    borderRadius: theme.spacing(1),
  },
  divider: {
    margin: theme.spacing(3, 0),
  },
  valuationDetailsTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  iconWrapper: {
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main + "1a",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(2),
  },
  icon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  title: {
    fontWeight: 600,
    lineHeight: 1.2,
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  searchBarWrapper: {
    maxWidth: 460,
  },
  valuationDetailsContent: {
    maxHeight: "60vh",
    overflowY: "auto",
    paddingRight: theme.spacing(1),
  },
}));

export const AddAppraisalDashboard = observer(() => {
  const classes = useStyles();
  const { addAppraisalStore } = useAddAppraisalStore();
  const { appraisalDetailsV2Store } = useStore();
  const { userStore } = useUserStore();
  const { showQuestionnaire } = appraisalDetailsV2Store;
  const loanId = addAppraisalStore.loan?.loanId;

  useEffect(() => {
    userStore.getUserInfoAndEntitlements();
  }, []);

  useEffect(() => {
    if (!loanId) {
      return;
    }
    (async () => {
      await appraisalDetailsV2Store.loanStore.fetchLoanDetails(loanId);
      appraisalDetailsV2Store.reset();
      appraisalDetailsV2Store.fetchAppraisalDetails(loanId);
    })();
  }, [loanId]);

  return (
    <Layout title="Add Appraisal" containerClass={classes.container}>
      <Paper className={classes.card}>
        <Box className={classes.header}>
          <Box className={classes.iconWrapper}>
            <Search className={classes.icon} />
          </Box>
          <Box>
            <Typography variant="subtitle1" className={classes.title}>
              Add Appraisal
            </Typography>
            <Typography variant="caption" className={classes.subtitle}>
              Please type the loan id to get started
            </Typography>
          </Box>
        </Box>
        <Box className={classes.searchBarWrapper}>
          <AddAppraisalSearchBar store={addAppraisalStore} />
        </Box>
        {loanId && (
          <>
            <Divider className={classes.divider} />
            <Box>
              <Typography variant="h6" className={classes.valuationDetailsTitle}>
                Valuation Details
              </Typography>
              <Box className={classes.valuationDetailsContent}>
                <AppraisalDetailsHeader store={appraisalDetailsV2Store} />
                <Box mt={2}>
                  {showQuestionnaire ? (
                    <QuestionnairePrompt store={appraisalDetailsV2Store} loanId={loanId} />
                  ) : (
                    <AppraisalDetails store={appraisalDetailsV2Store} loanId={loanId} />
                  )}
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Layout>
  );
});
